// ============================================================================
// Widget — Pending Filing — Litigation Aging (by age × case type)
// Litigation cases where the decision to litigate has been made but the
// plaint / petition has NOT yet been filed in court. Internally still called
// "Draft" (matches workflow case_status); UI label is "Pending Filing" per
// the management vocabulary used in NBFC MIS reviews.
//
// Stacked bars: one stack per age bucket, segments coloured by case type.
// Overlay: total outstanding (₹) per bucket on a secondary axis.
// ============================================================================
import React from 'react';
import ReactECharts from 'echarts-for-react';
import WidgetShell from './WidgetShell';
import {
  mockLitigationDraft,
  LITIGATION_CASE_TYPES,
  fmtAmount,
  fmtCount,
} from '../executiveMockData';

const LitigationDraftAgingWidget: React.FC = () => {
  const buckets = mockLitigationDraft;
  const labels  = buckets.map(b => b.ageLabel);

  // For each case-type colour band, build a series of counts across buckets.
  const series: any[] = LITIGATION_CASE_TYPES.map(ct => ({
    name: ct.label,
    type: 'bar',
    stack: 'count',
    yAxisIndex: 0,
    barWidth: 28,
    itemStyle: { color: ct.colour, borderRadius: [0, 0, 0, 0] },
    data: buckets.map(b => {
      const m = b.byType.find(t => t.caseType === ct.label);
      return m ? m.caseCount : 0;
    }),
  }));
  // Round the top of each stack — apply rounded corners only to the last series.
  series[series.length - 1].itemStyle = {
    ...series[series.length - 1].itemStyle,
    borderRadius: [3, 3, 0, 0],
  };

  // OS line on secondary axis — total across all types per bucket.
  const osTotalsPerBucket = buckets.map(b =>
    b.byType.reduce((acc, t) => acc + t.outstanding, 0),
  );
  series.push({
    name: 'Outstanding',
    type: 'line',
    yAxisIndex: 1,
    smooth: true,
    symbol: 'circle',
    symbolSize: 7,
    lineStyle: { width: 2, color: '#dc2626' },
    itemStyle: { color: '#dc2626' },
    z: 5,
    data: osTotalsPerBucket,
  });

  // Subtitle totals
  const totalCases = buckets.reduce(
    (sum, b) => sum + b.byType.reduce((s, t) => s + t.caseCount, 0), 0);
  const totalOs = osTotalsPerBucket.reduce((a, b) => a + b, 0);

  const option = {
    grid: { left: 44, right: 56, top: 28, bottom: 28 },
    legend: { show: true, top: 0, left: 0, fontSize: 10, itemHeight: 8, itemWidth: 12 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any[]) => {
        const idx = params[0].dataIndex;
        const b   = buckets[idx];
        const lines = b.byType
          .filter(t => t.caseCount > 0)
          .map(t => {
            const colour = LITIGATION_CASE_TYPES.find(c => c.label === t.caseType)?.colour ?? '#94a3b8';
            return `<span style="display:inline-block;width:8px;height:8px;background:${colour};margin-right:6px;border-radius:2px"></span>` +
                   `${t.caseType}: ${fmtCount(t.caseCount)} (${fmtAmount(t.outstanding)})`;
          }).join('<br/>');
        const totC = b.byType.reduce((s, t) => s + t.caseCount, 0);
        const totO = b.byType.reduce((s, t) => s + t.outstanding, 0);
        return `<strong>${b.ageLabel} days pending filing</strong><br/>${lines}<br/>` +
               `<strong>Total: ${fmtCount(totC)} cases · ${fmtAmount(totO)}</strong>`;
      },
    },
    xAxis: { type: 'category', data: labels, axisTick: { show: false }, axisLabel: { fontSize: 11 } },
    yAxis: [
      {
        type: 'value', name: 'Cases',
        nameTextStyle: { fontSize: 10, color: '#64748b' },
        axisLabel: { fontSize: 10 },
        splitLine: { lineStyle: { color: '#e5e7eb', type: 'dashed' } },
      },
      {
        type: 'value', name: '₹ Cr',
        nameTextStyle: { fontSize: 10, color: '#64748b' },
        axisLabel: { fontSize: 10, formatter: (v: number) => (v / 1_00_00_000).toFixed(1) },
        splitLine: { show: false },
      },
    ],
    series,
  };

  return (
    <WidgetShell
      title="Pending Filing — Age × Type"
      subtitle={`${fmtCount(totalCases)} cases · ${fmtAmount(totalOs)} OS awaiting court filing`}
    >
      <ReactECharts option={option} style={{ height: 240, width: '100%' }} />
    </WidgetShell>
  );
};

export default LitigationDraftAgingWidget;
